/**
 * Shopify Storefront API customer creation.
 *
 * Wired during the open-preview phase to capture leads from the in-app email
 * modal. Customers land in Customers → Email subscribers with acceptsMarketing
 * = true. Tagging happens server-side via a Shopify Flow rule (Storefront API
 * does not allow tag writes), keyed on signup date during the preview window.
 *
 * Token is a PUBLIC Storefront API access token — designed to be embedded in
 * client-side JavaScript. Not a secret. Only grants the scopes we configured
 * (unauthenticated_read_customers + unauthenticated_write_customers).
 */

const STOREFRONT_TOKEN = '6790fc2df4611eab59d13f0927db05d9';
const STOREFRONT_ENDPOINT = 'https://sparrowexpeditions.com/api/2024-10/graphql.json';

interface CustomerCreateResult {
  ok: boolean;
  /** True if customer was newly created. False if email already existed (still counts as "captured" for UX). */
  created: boolean;
  /** Human-readable status; surfaced to the user only on hard failures. */
  status: 'created' | 'already_subscribed' | 'invalid_email' | 'network_error';
  errorCode?: string;
}

/**
 * Create a Shopify customer record from email + name.
 *
 * UX contract: never block the user on this. Returns a result object the
 * caller can use to decide what UI to show, but the modal should treat
 * 'already_subscribed' the same as 'created' — both mean "we have them."
 */
export async function createStorefrontCustomer(
  email: string,
  fullName: string,
): Promise<CustomerCreateResult> {
  if (!email || !email.includes('@')) {
    return { ok: false, created: false, status: 'invalid_email' };
  }

  // Split "First Last" into firstName / lastName; everything past the first
  // space goes into lastName. If no space, lastName stays blank.
  const trimmed = fullName.trim();
  const spaceIdx = trimmed.indexOf(' ');
  const firstName = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
  const lastName = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1);

  // Random password — the user never sees it. If they ever want to log in,
  // they reset via /account/login. Crypto-random so we're not vulnerable to
  // guessable defaults if a Shopify admin export ever leaks.
  const password = generateRandomPassword(24);

  const mutation = `
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer { id email firstName lastName acceptsMarketing }
        customerUserErrors { code field message }
      }
    }
  `;

  const variables = {
    input: {
      email,
      password,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      acceptsMarketing: true,
    },
  };

  try {
    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        'Accept': 'application/json',
      },
      body: JSON.stringify({ query: mutation, variables }),
    });

    if (!res.ok) {
      return { ok: false, created: false, status: 'network_error' };
    }

    const json = await res.json();
    const errors = json?.data?.customerCreate?.customerUserErrors ?? [];

    if (errors.length === 0) {
      return { ok: true, created: true, status: 'created' };
    }

    // Existing-customer collision is the normal "already captured" path —
    // treat as success for UX. CUSTOMER_DISABLED happens when an email
    // exists but is unconfirmed; TAKEN happens when already confirmed.
    const code: string | undefined = errors[0]?.code;
    if (code === 'CUSTOMER_DISABLED' || code === 'TAKEN' || code === 'UNIDENTIFIED_CUSTOMER') {
      return { ok: true, created: false, status: 'already_subscribed', errorCode: code };
    }

    return { ok: false, created: false, status: 'network_error', errorCode: code };
  } catch (e) {
    return { ok: false, created: false, status: 'network_error' };
  }
}

/**
 * 24-character crypto-random alphanumeric password. The customer never sees
 * this — it exists so Shopify accepts the customerCreate input. Real auth
 * goes through Shopify's password-reset flow if the customer ever wants to
 * log in.
 */
function generateRandomPassword(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    // Fallback for environments without WebCrypto (very old browsers).
    let out = '';
    for (let i = 0; i < length; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  }
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < length; i++) out += chars[arr[i] % chars.length];
  return out;
}
